// Shared gesture state for the mobile map sheet. Native content scrolling is
// retained; only explicit drag surfaces disable the browser's touch panning.
const SNAP_NAMES = ['full', 'half', 'peek'];
const DRAG_ZONES = '.nm-sheet-handle, .nm-tabs, .nm-selection-banner, .nm-sheet-preview';
const INTERACTIVE = 'button, a, input, select, textarea, summary, [contenteditable], [role="button"], [role="tab"], [role="slider"], [data-sheet-no-drag]';
const SLOP = 6;
const FLICK_VELOCITY = 500;

function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
}

export function resolveSheetSnap({ y, startY, velocity = 0, currentSnap = 'half', breaks }) {
    const positions = SNAP_NAMES.map(name => ({ name, y: breaks[name] }))
        .sort((a, b) => a.y - b.y);
    const nearest = value => positions.reduce((best, position) =>
        Math.abs(position.y - value) < Math.abs(best.y - value) ? position : best
    ).name;
    const delta = y - startY;
    if (Math.abs(delta) < SLOP) return currentSnap;
    const direction = Math.sign(delta);
    // The target of an interrupted transition need not be the visible origin.
    // Use the first snap ahead of the finger, rather than stepping from that
    // stale target (which can skip the middle state in the wrong direction).
    const ahead = positions.filter(position => direction * (position.y - startY) > SLOP)
        .sort((a, b) => Math.abs(a.y - startY) - Math.abs(b.y - startY));
    if (!ahead.length) return nearest(y);
    const next = ahead[0];
    const inDirection = name => direction * (breaks[name] - startY) > SLOP;

    if (Math.abs(velocity) >= FLICK_VELOCITY && Math.sign(velocity) === direction) {
        const projected = clamp(y + clamp(velocity * 0.12, -100, 100), positions[0].y, positions[positions.length - 1].y);
        const target = nearest(projected);
        return inDirection(target) ? target : next.name;
    }

    const threshold = Math.min(100, Math.max(24, Math.abs(next.y - startY) * 0.25));
    if (Math.abs(delta) < threshold) return currentSnap;
    const target = nearest(y);
    return inDirection(target) ? target : next.name;
}

export function createSheetGestureController(config) {
    const { sheetEl, contentEl, handleEl } = config;
    const eventTarget = config.eventTarget || window;
    let initialized = false;
    let active = null;
    let suppressClick = false;
    let suppressTarget = null;
    let suppressionTimer = null;
    let restoreHandleTabIndex = null;

    function syncA11y() {
        if (!handleEl) return;
        const snap = config.getCurrentSnap();
        const labels = { full: 'rozwinięty', half: 'częściowo rozwinięty', peek: 'zwinięty' };
        handleEl.setAttribute('aria-expanded', snap === 'peek' ? 'false' : 'true');
        handleEl.setAttribute('aria-label', 'Panel szczegółów: ' + labels[snap] + '. Strzałki zmieniają wysokość.');
    }

    function step(direction) {
        const index = SNAP_NAMES.indexOf(config.getCurrentSnap());
        const next = SNAP_NAMES[clamp(index + direction, 0, 2)];
        if (next) config.snapTo(next);
        syncA11y();
    }

    function onHandleClick(e) {
        if (config.isDesktopLayout()) return;
        if (suppressClick && isSuppressedTarget(e.target)) {
            e.preventDefault();
            e.stopPropagation?.();
            clearSuppression();
            return;
        }
        step(config.getCurrentSnap() === 'full' ? 2 : -1);
    }

    function onHandleKeydown(e) {
        if (config.isDesktopLayout()) return;
        let target = null;
        if (e.key === 'ArrowUp') target = SNAP_NAMES[clamp(SNAP_NAMES.indexOf(config.getCurrentSnap()) - 1, 0, 2)];
        else if (e.key === 'ArrowDown') target = SNAP_NAMES[clamp(SNAP_NAMES.indexOf(config.getCurrentSnap()) + 1, 0, 2)];
        else if (e.key === 'Home') target = 'full';
        else if (e.key === 'End') target = 'peek';
        else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            // Native buttons synthesize click for these keys; this is a div.
            onHandleClick(e);
            return;
        }
        if (target) {
            e.preventDefault();
            config.snapTo(target);
            syncA11y();
        }
    }

    function clearSuppression() {
        suppressClick = false;
        suppressTarget = null;
        clearTimeout(suppressionTimer);
        suppressionTimer = null;
    }

    function suppressNextClick(target) {
        suppressClick = true;
        suppressTarget = target;
        clearTimeout(suppressionTimer);
        suppressionTimer = setTimeout(clearSuppression, 500);
    }

    function isSuppressedTarget(target) {
        return suppressTarget && (target === suppressTarget
            || suppressTarget.contains?.(target));
    }

    function onClickCapture(e) {
        if (!suppressClick) return;
        if (!isSuppressedTarget(e.target)) { clearSuppression(); return; }
        e.preventDefault();
        e.stopImmediatePropagation?.();
        e.stopPropagation?.();
        clearSuppression();
    }

    function isWithinSheet(target) {
        return target && sheetEl.contains(target);
    }

    function dragZone(target) {
        const zone = target?.closest?.(DRAG_ZONES);
        return zone && isWithinSheet(zone) ? zone : null;
    }

    function isExcluded(target) {
        const interactive = target?.closest?.(INTERACTIVE);
        return !!interactive && interactive !== handleEl;
    }

    function start(kind, id, x, y, time, target, captureTarget = null) {
        if (active || config.isDesktopLayout()) return false;
        clearSuppression();
        const origin = config.interrupt();
        active = {
            kind, id, target, captureTarget, startX: x, startY: y,
            startSheetY: origin, originSnap: config.getCurrentSnap(),
            direction: null, moving: false, scrolling: false,
            samples: [{ y, time }], lastY: y, lastTime: time,
        };
        if (captureTarget?.setPointerCapture) {
            try { captureTarget.setPointerCapture(id); } catch (_) { /* detached target */ }
        }
        return true;
    }

    function samplesFor(activeGesture, y, time) {
        const samples = activeGesture.samples;
        samples.push({ y, time });
        while (samples.length > 2 && samples[1].time < time - 120) samples.shift();
        activeGesture.lastY = y;
        activeGesture.lastTime = time;
    }

    function releaseVelocity(gesture, time) {
        if (time - gesture.lastTime > 100) return 0;
        const samples = gesture.samples;
        const latest = samples[samples.length - 1];
        const earliest = samples[0];
        const dt = latest.time - earliest.time;
        return dt > 0 ? clamp((latest.y - earliest.y) / dt * 1000, -2500, 2500) : 0;
    }

    function canScroll(target, deltaY) {
        for (let el = target; el && contentEl.contains(el); el = el.parentElement) {
            const style = getComputedStyle(el);
            if (!/(auto|scroll)/.test(style.overflowY)) continue;
            if (el === contentEl && config.getCurrentSnap() !== 'full') continue;
            if (el.scrollHeight <= el.clientHeight + 1) continue;
            if (deltaY > 0 && el.scrollTop > 1) return true;
            if (deltaY < 0 && el.scrollTop + el.clientHeight < el.scrollHeight - 1) return true;
        }
        return false;
    }

    function move(kind, id, x, y, time, e) {
        const gesture = active;
        if (!gesture || gesture.kind !== kind || gesture.id !== id) return;
        if (config.isDesktopLayout()) { cancel(); return; }
        // Native scrolling cannot be taken over after the browser commits it.
        if (kind === 'touch' && !e.cancelable) {
            finish(kind, id, time, true);
            return;
        }
        const deltaX = x - gesture.startX;
        const deltaY = y - gesture.startY;
        // Reserve an upward gesture before native nested scrolling can claim it.
        // A short first touchmove may precede the direction-lock threshold.
        const expanding = gesture.originSnap !== 'full' && deltaY < 0 && Math.abs(deltaY) > Math.abs(deltaX);
        if (kind === 'touch' && expanding && e.cancelable) e.preventDefault();
        if (!gesture.direction) {
            if (Math.abs(deltaX) < SLOP && Math.abs(deltaY) < SLOP) return;
            gesture.direction = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
        }
        if (gesture.direction === 'horizontal') {
            gesture.cancelClick = true;
            return;
        }

        if (kind === 'touch' && !gesture.moving) {
            if (!expanding && canScroll(gesture.target, deltaY)) {
                gesture.scrolling = true;
                return;
            }
            // Preserve the native scroll already consumed before a boundary.
            if (gesture.scrolling) {
                gesture.startY = y;
                gesture.startSheetY = config.interrupt();
            }
        }
        if (!gesture.moving) {
            gesture.moving = true;
            if (kind === 'touch' && !gesture.scrolling) {
                gesture.startSheetY = config.interrupt();
            }
            if (kind === 'touch' && gesture.scrolling) gesture.samples = [{ y, time }];
        }
        if (e.cancelable) e.preventDefault();
        samplesFor(gesture, y, time);
        config.dragTo(gesture.startSheetY + y - gesture.startY);
    }

    function finish(kind, id, time, cancelled = false) {
        const gesture = active;
        if (!gesture || gesture.kind !== kind || gesture.id !== id) return;
        active = null;
        if (gesture.captureTarget?.hasPointerCapture?.(id)) {
            try { gesture.captureTarget.releasePointerCapture(id); } catch (_) { /* already lost */ }
        }
        if (gesture.moving) {
            const velocity = cancelled ? 0 : releaseVelocity(gesture, time);
            const snap = resolveSheetSnap({
                y: config.getSheetY(), startY: gesture.startSheetY,
                velocity, currentSnap: gesture.originSnap, breaks: config.getBreaks(),
            });
            config.snapTo(snap);
            suppressNextClick(dragZone(gesture.target) || gesture.target);
            syncA11y();
        } else {
            if (gesture.cancelClick) suppressNextClick(dragZone(gesture.target) || gesture.target);
            if (cancelled || Math.abs(config.getSheetY() - config.getBreaks()[gesture.originSnap]) > 1) {
                config.snapTo(gesture.originSnap);
                syncA11y();
            }
        }
    }

    function onPointerDown(e) {
        if (active) {
            if (active.kind === 'pointer' && active.id !== e.pointerId) {
                finish('pointer', active.id, e.timeStamp, true);
            }
            return;
        }
        if (e.isPrimary === false || e.button !== 0) return;
        const zone = dragZone(e.target);
        if (!zone || isExcluded(e.target, zone)) return;
        start('pointer', e.pointerId, e.clientX, e.clientY, e.timeStamp, e.target, e.target);
    }

    function onPointerMove(e) {
        move('pointer', e.pointerId, e.clientX, e.clientY, e.timeStamp, e);
    }

    function onAdditionalPointerDown(e) {
        if (!active && e.isPrimary !== false) clearSuppression();
        if (active?.kind === 'pointer' && active.id !== e.pointerId) {
            finish('pointer', active.id, e.timeStamp, true);
        }
    }

    function onPointerUp(e) {
        finish('pointer', e.pointerId, e.timeStamp, e.type !== 'pointerup');
    }

    function findTouch(e, id, changed = false) {
        return Array.from(changed ? e.changedTouches : e.touches).find(touch => touch.identifier === id);
    }

    function onTouchStart(e) {
        if (active && active.kind === 'touch' && e.touches.length !== 1) {
            finish('touch', active.id, e.timeStamp, true);
            return;
        }
        if (active || config.isDesktopLayout() || e.touches.length !== 1) return;
        clearSuppression();
        if (dragZone(e.target)) return;
        const editable = e.target?.closest?.('input, select, textarea, [contenteditable], [role="slider"], [data-sheet-no-drag]');
        if (editable || (config.getCurrentSnap() === 'full' && isExcluded(e.target))) return;
        const touch = e.touches[0];
        // Do not stop an in-progress animation for an ordinary content tap.
        active = {
            kind: 'touch', id: touch.identifier, target: e.target,
            startX: touch.clientX, startY: touch.clientY, startSheetY: config.getSheetY(),
            originSnap: config.getCurrentSnap(), direction: null, moving: false, scrolling: false,
            samples: [{ y: touch.clientY, time: e.timeStamp }],
            lastY: touch.clientY, lastTime: e.timeStamp,
        };
    }

    function onTouchMove(e) {
        if (!active || active.kind !== 'touch') return;
        if (e.touches.length !== 1) { finish('touch', active.id, e.timeStamp, true); return; }
        const touch = findTouch(e, active.id);
        if (touch) move('touch', touch.identifier, touch.clientX, touch.clientY, e.timeStamp, e);
    }

    function onTouchEnd(e) {
        if (!active || active.kind !== 'touch') return;
        const touch = findTouch(e, active.id, true);
        if (touch) finish('touch', touch.identifier, e.timeStamp, e.type !== 'touchend');
    }

    function cancel() {
        if (!active) return;
        const gesture = active;
        active = null;
        if (gesture.captureTarget?.hasPointerCapture?.(gesture.id)) {
            try { gesture.captureTarget.releasePointerCapture(gesture.id); } catch (_) { /* already lost */ }
        }
        clearSuppression();
    }

    function onLostPointerCapture(e) {
        if (active?.kind === 'pointer' && active.id === e.pointerId) {
            finish('pointer', e.pointerId, e.timeStamp, true);
        }
    }

    function onBlur() {
        if (!active) return;
        const gesture = active;
        finish(gesture.kind, gesture.id, gesture.lastTime + 101, true);
    }

    function init() {
        if (initialized || !sheetEl) return;
        initialized = true;
        sheetEl.addEventListener('pointerdown', onPointerDown);
        sheetEl.addEventListener('click', onClickCapture, true);
        eventTarget.addEventListener('pointerdown', onAdditionalPointerDown, true);
        eventTarget.addEventListener('pointermove', onPointerMove);
        eventTarget.addEventListener('pointerup', onPointerUp);
        eventTarget.addEventListener('pointercancel', onPointerUp);
        eventTarget.addEventListener('lostpointercapture', onLostPointerCapture, true);
        eventTarget.addEventListener('blur', onBlur);
        if (contentEl) {
            contentEl.addEventListener('touchstart', onTouchStart, { passive: true });
            eventTarget.addEventListener('touchmove', onTouchMove, { passive: false });
            eventTarget.addEventListener('touchend', onTouchEnd);
            eventTarget.addEventListener('touchcancel', onTouchEnd);
        }
        if (handleEl) {
            handleEl.setAttribute('role', 'button');
            restoreHandleTabIndex = handleEl.getAttribute('tabindex');
            handleEl.setAttribute('tabindex', '0');
            handleEl.addEventListener('click', onHandleClick);
            handleEl.addEventListener('keydown', onHandleKeydown);
        }
        syncA11y();
    }

    function destroy() {
        if (!initialized) return;
        cancel();
        clearSuppression();
        initialized = false;
        sheetEl.removeEventListener('pointerdown', onPointerDown);
        sheetEl.removeEventListener('click', onClickCapture, true);
        eventTarget.removeEventListener('pointerdown', onAdditionalPointerDown, true);
        eventTarget.removeEventListener('pointermove', onPointerMove);
        eventTarget.removeEventListener('pointerup', onPointerUp);
        eventTarget.removeEventListener('pointercancel', onPointerUp);
        eventTarget.removeEventListener('lostpointercapture', onLostPointerCapture, true);
        eventTarget.removeEventListener('blur', onBlur);
        if (contentEl) {
            contentEl.removeEventListener('touchstart', onTouchStart);
            eventTarget.removeEventListener('touchmove', onTouchMove);
            eventTarget.removeEventListener('touchend', onTouchEnd);
            eventTarget.removeEventListener('touchcancel', onTouchEnd);
        }
        if (handleEl) {
            handleEl.removeEventListener('click', onHandleClick);
            handleEl.removeEventListener('keydown', onHandleKeydown);
            if (restoreHandleTabIndex === null) handleEl.removeAttribute?.('tabindex');
            else handleEl.setAttribute('tabindex', restoreHandleTabIndex);
        }
    }

    return { init, cancel, syncA11y, destroy, isDragging: () => !!active?.moving };
}
